import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AppIcon } from "@/components/Brand";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_NAMES = {
  founding: "Founding Instructor",
  instructor: "DriveAssure Instructor",
  pro: "DriveAssure Pro",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "verifying", tier: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed", tier: null });
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setState({ phase: "paid", tier: data.tier });
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      tries += 1;
      if (tries < 20) timer = setTimeout(poll, 2000);
      else setState({ phase: "timeout", tier: null });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#1E1E1E] text-white flex items-center justify-center px-5">
      <div className="max-w-lg w-full text-center" data-testid="payment-success-page">
        <span className="inline-grid place-items-center w-20 h-20 rounded-[1.4rem] bg-[#2BBFB3]/15 border border-[#2BBFB3]/30">
          <AppIcon size={48} />
        </span>

        {state.phase === "verifying" && (
          <div data-testid="payment-verifying">
            <Loader2 size={36} className="mx-auto mt-8 animate-spin text-[#2BBFB3]" />
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">Confirming your subscription…</h1>
            <p className="mt-3 text-white/60">Hold tight — we're verifying your payment with Stripe.</p>
          </div>
        )}

        {state.phase === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={44} className="mx-auto mt-8 text-[#2BBFB3]" />
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
              Welcome aboard{state.tier ? `, ${TIER_NAMES[state.tier] || ""}` : ""}.
            </h1>
            <p className="mt-3 text-white/60">Your subscription is active. Here's how to unlock it in the app:</p>
            <ol className="mt-7 text-left space-y-3">
              {[
                "Download DriveAssure on your phone",
                "Sign in with the same email you used at checkout",
                "Your plan is recognised automatically — no codes needed",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-[#2BBFB3] text-[#0f1211] text-sm font-extrabold shrink-0">{i + 1}</span>
                  <span className="text-white/80 text-sm leading-relaxed pt-1">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                data-testid="payment-success-home"
                className="inline-flex items-center justify-center rounded-full bg-[#2BBFB3] px-6 py-3 text-sm font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
              >
                Back to DriveAssure
              </Link>
              <a
                href="/#download"
                data-testid="payment-success-download"
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Download the app
              </a>
            </div>
          </div>
        )}

        {(state.phase === "failed" || state.phase === "timeout") && (
          <div data-testid="payment-unverified">
            <XCircle size={44} className="mx-auto mt-8 text-[#F5A623]" />
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
              {state.phase === "timeout" ? "Still confirming…" : "We couldn't verify that payment"}
            </h1>
            <p className="mt-3 text-white/60">
              {state.phase === "timeout"
                ? "Your payment is taking longer than usual to confirm. If you were charged, your subscription will activate automatically — check your email for the Stripe receipt."
                : "No charge was made. Please try subscribing again from the pricing section."}
            </p>
            <Link
              to="/"
              data-testid="payment-failed-home"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[#2BBFB3] px-6 py-3 text-sm font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
            >
              Back to DriveAssure
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
